// Google Analytics 4 event tracking utility

import { trackMetaPixelContact } from '@/lib/meta-pixel';

export interface GA4EventParams {
  [key: string]: unknown;
}

const CTA_ID_MAP: Record<string, string> = {
  'book a free consultation': 'consultation',
  'book a consultation': 'consultation',
  'free consultation': 'consultation',
  'consultation': 'consultation',
  'δωρεάν συμβουλευτική': 'consultation',
  'κλείστε δωρεάν συμβουλευτική': 'consultation',
  'κλείστε συμβουλευτική': 'consultation',
  'ready to start': 'consultation',
  'contact us': 'contact',
  'contact': 'contact',
  'επικοινωνήστε μαζί μας': 'contact',
  'επικοινωνια': 'contact',
  'επικοινωνία': 'contact',
  'view our services': 'services',
  'δείτε τις υπηρεσίες μας': 'services',
  'services': 'services',
  'view case studies': 'case_studies',
  'see all case studies': 'case_studies',
  'δείτε case studies': 'case_studies',
  'case studies': 'case_studies',
  'view our work': 'portfolio',
  'portfolio': 'portfolio',
  'understanding marketing costs': 'marketing_cost',
  'κατανόηση του κόστους μάρκετινγκ': 'marketing_cost',
  'package selection': 'package_selection',
  'επιλογή πακέτου': 'package_selection',
};

const normalizeCtaText = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^\p{L}\p{N} ]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const inferCtaId = (ctaText: string) => CTA_ID_MAP[normalizeCtaText(ctaText)];

export const track = (name: string, params: GA4EventParams = {}) => {
  const gtag = (globalThis as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof window !== 'undefined' && gtag) {
    gtag('event', name, params);
  }
};

// Event tracking functions
export const trackServiceView = (serviceSlug: string, language: string = 'el') => {
  track('view_service', {
    service_slug: serviceSlug,
    language: language
  });
};

export const trackCTA = (ctaText: string, pagePath: string, variant?: string) => {
  track('cta_click', {
    cta_text: ctaText,
    cta_id: inferCtaId(ctaText),
    variant: variant || 'primary',
    page_path: pagePath
  });
};

export const trackCTAClick = (
  ctaText: string,
  section: string,
  variant: string = 'primary',
  ctaId?: string
) => {
  track('cta_click', {
    cta_text: ctaText,
    cta_section: section,
    variant: variant,
    cta_id: ctaId ?? inferCtaId(ctaText),
    page_path: location.pathname
  });
};

export const trackFormSubmit = (formId: string, pagePath: string, success: boolean = true) => {
  track('submit_form', {
    form_id: formId,
    page_path: pagePath,
    success: success
  });
};

export const trackScroll75 = (pagePath: string) => {
  track('scroll_75', {
    page_path: pagePath
  });
};

export const trackCaseStudyView = (slug: string) => {
  track('view_case_study', {
    slug: slug
  });
};

// Consent management
export const updateConsent = (granted: boolean) => {
  const gtag = (globalThis as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof window !== 'undefined' && gtag) {
    gtag('consent', 'update', {
      'analytics_storage': granted ? 'granted' : 'denied',
      'ad_storage': granted ? 'granted' : 'denied',
      'ad_user_data': granted ? 'granted' : 'denied',
      'ad_personalization': granted ? 'granted' : 'denied'
    });
  }
};

// Check if gtag is available
export const isGtagAvailable = (): boolean => {
  return typeof window !== 'undefined' && !!(globalThis as { gtag?: (...args: unknown[]) => void }).gtag;
};

// Phone click tracking
export const bindPhoneClicks = () => {
  document.querySelectorAll('a[href^="tel:"]').forEach(a => {
    a.addEventListener('click', () => {
      const phone = (a as HTMLAnchorElement).href.replace('tel:', '');
      track('phone_click', { phone, page_path: location.pathname });
    });
  });
};

let lastContactTrackTs = 0;

export const bindContactLinks = () => {
  document.addEventListener('click', (e) => {
    const a = (e.target as HTMLElement).closest('a');
    if (!a) return;

    const href = a.getAttribute('href') || '';
    const now = Date.now();
    if (now - lastContactTrackTs < 500) return;

    if (href.startsWith('tel:')) {
      lastContactTrackTs = now;
      const phone = href.replace('tel:', '');
      track('phone_click', { phone, page_path: location.pathname });
      trackMetaPixelContact({ channel: 'phone', value: phone, page_path: location.pathname });
      return;
    }

    if (href.startsWith('mailto:')) {
      lastContactTrackTs = now;
      const email = href.replace('mailto:', '');
      track('email_click', { email, page_path: location.pathname });
      trackMetaPixelContact({ channel: 'email', value: email, page_path: location.pathname });
    }
  }, { capture: true });
};

// Outbound link tracking
export const bindOutboundLinks = () => {
  document.addEventListener('click', (e) => {
    const a = (e.target as HTMLElement).closest('a');
    if (!a) return;

    const url = new URL(a.href, location.origin);
    if (url.host !== location.host) {
      track('outbound_click', { href: url.href, page_path: location.pathname });
    }
  }, { capture: true });
};

// Form abandonment tracking
let formTouched = false;
let startTs = Date.now();

export const bindFormAbandon = (formSelector: string, formId: string) => {
  const form = document.querySelector<HTMLFormElement>(formSelector);
  if (!form) return;

  form.addEventListener('input', () => {
    formTouched = true;
  }, { once: true });

  form.addEventListener('submit', () => {
    formTouched = false;
  });

  const handler = () => {
    if (document.visibilityState === 'hidden' && formTouched) {
      track('form_abandon', {
        form_id: formId,
        time_on_page: Math.round((Date.now() - startTs) / 1000),
        page_path: location.pathname
      });
    }
  };

  document.addEventListener('visibilitychange', handler);
};

// Initialize form abandonment tracking for contact forms
export const initFormAbandonmentTracking = () => {
  // Track contact forms on service pages
  bindFormAbandon('#contact-form', 'contact');

  // Reset timestamp on page load
  startTs = Date.now();
  formTouched = false;
};
